"use client";
import { useCallback, useEffect, useState } from "react";
import { useAuthedApi } from "../api/useAuthedApi";
import type { MarketplacePackage } from "../api/marketplaceApi";
import type { ConnectorListing } from "../api/connectorsApi";
import { useOAuthPopup } from "./useOAuthPopup";

/** Drives MarketplaceBrowser: loads the listed registry catalog plus the
 *  tenant's installed flags, and offers an ``install`` action that posts
 *  to the SP-E install-from-registry endpoint and refreshes the list.
 *  Also loads per-user connector OAuth state (best-effort) and offers
 *  ``connect``/``disconnect`` actions for OAuth-backed plugins. */
export function useMarketplace() {
  const { marketplace, connectors } = useAuthedApi();
  const [packages, setPackages] = useState<MarketplacePackage[] | null>(null);
  const [connectorState, setConnectorState] = useState<
    Record<string, ConnectorListing["state"]>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!marketplace) return;
    try {
      setError(null);
      setPackages(await marketplace.list());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load marketplace");
      // Fall through with [] so the component leaves its loading state and
      // surfaces the Alert instead of spinning forever.
      setPackages([]);
    }
    // Connector state best-effort: empty list (OAuth disabled / 404) or
    // any failure just means no Connect buttons — never page error.
    try {
      const rows = connectors ? await connectors.list() : [];
      setConnectorState(
        Object.fromEntries(rows.map((c) => [c.name, c.state])),
      );
    } catch {
      setConnectorState({});
    }
  }, [marketplace, connectors]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const install = useCallback(
    async (
      name: string,
      config: { components: Record<string, string[]> } = { components: {} },
      scope?: "company" | "user",
    ) => {
      if (!marketplace) return;
      setBusy(name);
      setError(null);
      setNotice(null);
      try {
        await marketplace.install(name, undefined, config, { scope });
        await refresh();
        // Honest, state-aware message: a turn re-resolves installed plugins
        // per sync, so the change applies to new conversations — not an
        // in-flight one. The message IS the mechanism (no re-sync endpoint).
        setNotice(
          scope === "user"
            ? "Installed for you — active in new conversations."
            : "Installed — active in new conversations.",
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : `Install of ${name} failed`);
      } finally {
        setBusy(null);
      }
    },
    [marketplace, refresh],
  );

  const updateConfig = useCallback(
    async (name: string, components: Record<string, string[]>) => {
      if (!marketplace) return;
      setBusy(name);
      setError(null);
      setNotice(null);
      try {
        await marketplace.updateConfig(name, components);
        await refresh();
        setNotice("Updated — applies to new conversations.");
      } catch (e) {
        setError(
          e instanceof Error ? e.message : `Update of ${name} failed`,
        );
      } finally {
        setBusy(null);
      }
    },
    [marketplace, refresh],
  );

  const uninstall = useCallback(
    async (name: string) => {
      if (!marketplace) return;
      setBusy(name);
      setError(null);
      setNotice(null);
      try {
        await marketplace.uninstall(name);
        await refresh();
        setNotice("Uninstalled — removed from new conversations.");
      } catch (e) {
        setError(
          e instanceof Error ? e.message : `Uninstall of ${name} failed`,
        );
      } finally {
        setBusy(null);
      }
    },
    [marketplace, refresh],
  );

  const openPopup = useOAuthPopup({
    authorize: async (slug: string) => connectors!.authorize(slug),
  });

  const connect = useCallback(
    async (slug: string) => {
      if (!connectors) return;
      setBusy(slug);
      setError(null);
      setNotice(null);
      try {
        await openPopup(slug);
        await refresh();
        setNotice("Connected.");
      } catch (e) {
        setError(e instanceof Error ? e.message : `Connect ${slug} failed`);
      } finally {
        setBusy(null);
      }
    },
    [connectors, openPopup, refresh],
  );

  const disconnect = useCallback(
    async (slug: string) => {
      if (!connectors) return;
      setBusy(slug);
      setError(null);
      setNotice(null);
      try {
        await connectors.disconnect(slug);
        await refresh();
        setNotice("Disconnected.");
      } catch (e) {
        setError(e instanceof Error ? e.message : `Disconnect ${slug} failed`);
      } finally {
        setBusy(null);
      }
    },
    [connectors, refresh],
  );

  return {
    packages,
    connectorState,
    refresh,
    install,
    uninstall,
    updateConfig,
    connect,
    disconnect,
    error,
    notice,
    busy,
  };
}
